// apps/web/src/components/InstallAppPrompt.tsx
//
// Nudge de instalação do PWA no login (NÃO-fixo). Substitui o botão fixo da
// topbar: ao entrar no app autenticado, se ainda não instalado e não sonecado,
// mostra UM toast (canto) — com ação de instalar (Chromium, 1 clique via
// prompt nativo) ou instrução manual (iOS Safari, Compartilhar → Adicionar à
// Tela de Início). "Agora não" soneca 7 dias. Some sozinho se já instalado ou
// se a plataforma não tem caminho de instalação. O acesso permanente fica no
// menu do avatar (UserMenuDropdown), que reusa `showInstallToast`.

import { useEffect } from 'react';

import type { TFunction } from 'i18next';
import { Download, Share } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Button } from '@alamo/ui';

import {
  getInstallMode,
  isInstallPromptSnoozed,
  promptInstall,
  snoozeInstallPrompt,
  subscribeInstallPrompt,
  type InstallMode,
} from '../lib/installPrompt';

const INSTALL_TOAST_ID = 'app-install-prompt';

/**
 * Mostra o toast de instalação para o modo dado. Reusado pelo menu do avatar.
 * `prompt` → CTA que dispara o prompt nativo; `ios-safari` → instrução manual.
 */
export function showInstallToast(mode: Exclude<InstallMode, 'none'>, t: TFunction): void {
  toast(
    (instance) => (
      <div className="flex items-start gap-3">
        {mode === 'prompt' ? (
          <Download className="mt-0.5 size-5 shrink-0 text-laranja-encanto" aria-hidden />
        ) : (
          <Share className="mt-0.5 size-5 shrink-0 text-laranja-encanto" aria-hidden />
        )}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-ink">
            {mode === 'prompt' ? t('ui:pwa.install_title') : t('ui:pwa.install_ios_title')}
          </span>
          {mode === 'ios-safari' && (
            <span className="text-xs text-slate-600">{t('ui:pwa.install_ios_hint')}</span>
          )}
          <div className="flex items-center gap-2">
            {mode === 'prompt' ? (
              <Button
                size="sm"
                onClick={() => {
                  toast.dismiss(instance.id);
                  void promptInstall();
                }}
              >
                {t('ui:pwa.install_action')}
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => toast.dismiss(instance.id)}>
                {t('ui:pwa.install_ios_ack')}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                snoozeInstallPrompt();
                toast.dismiss(instance.id);
              }}
            >
              {t('ui:pwa.install_later')}
            </Button>
          </div>
        </div>
      </div>
    ),
    { id: INSTALL_TOAST_ID, duration: Infinity },
  );
}

// Dispara no máximo 1x por carregamento (login/refresh). O guard evita
// re-disparo em re-render / StrictMode; a soneca de 7d suprime após "Agora não".
let shownThisLoad = false;

/** Reset apenas para teste (o módulo é importado uma vez e nunca reinicia). */
export function __resetInstallPromptShownForTest(): void {
  shownThisLoad = false;
}

/**
 * Efeito global do nudge de instalação. Montar dentro do shell autenticado
 * (AppLayout). Em Chromium o `beforeinstallprompt` pode chegar DEPOIS do mount,
 * então assina o store e reavalia; em iOS Safari o modo já é síncrono.
 */
export function InstallAppPrompt(): null {
  const { t } = useTranslation(['ui']);

  useEffect(() => {
    const maybeShow = (): void => {
      if (shownThisLoad || isInstallPromptSnoozed()) return;
      const mode = getInstallMode();
      if (mode === 'none') return;
      shownThisLoad = true;
      showInstallToast(mode, t);
    };

    const unsubscribe = subscribeInstallPrompt(maybeShow);
    maybeShow();
    return unsubscribe;
  }, [t]);

  return null;
}
